/**
 * Selection rules of DocumentSelectorModal. The selection holds whole document
 * objects in pick order, so documents picked in one folder stay selected (and
 * counted) while the user browses another one.
 */

interface Identified {
  id: string
}

/** Click on a document: multi-select toggles it; single-select makes it the only pick. */
export function toggleDocument<T extends Identified>(selection: T[], doc: T, multiple: boolean): T[] {
  const picked = selection.some((d) => d.id === doc.id)
  if (!multiple) return picked && selection.length === 1 ? selection : [doc]
  return picked ? selection.filter((d) => d.id !== doc.id) : selection.concat(doc)
}

/** The header checkbox: select or clear every document of the open folder, leaving other folders' picks alone. */
export function setFolderDocuments<T extends Identified>(selection: T[], folderDocs: T[], checked: boolean): T[] {
  const folderIds = new Set(folderDocs.map((d) => d.id))
  if (!checked) return selection.filter((d) => !folderIds.has(d.id))
  const selectedIds = new Set(selection.map((d) => d.id))
  return selection.concat(folderDocs.filter((d) => !selectedIds.has(d.id)))
}

/** State of the header checkbox for the open folder. */
export function folderSelectionState<T extends Identified>(selection: T[], folderDocs: T[]): boolean | 'indeterminate' {
  if (folderDocs.length === 0) return false
  const selectedIds = new Set(selection.map((d) => d.id))
  const count = folderDocs.filter((d) => selectedIds.has(d.id)).length
  if (count === 0) return false
  return count === folderDocs.length ? true : 'indeterminate'
}
